/**
 * Scheduler Runtime Types
 *
 * Types used by the EnhancedPlaylistScheduler for runtime evaluation.
 * These are separate from schedule configuration types (schedule.types.ts)
 *
 * Azure Best Practice: Separation of concerns - config types vs runtime types
 */

import { Playlist } from "./playlist.types";
import { PrayerTimes } from "./PrayerTypes";
import { PriorityLevel, ScheduleType } from "./schedule.types";
import { ScreenData } from "./ScreenTypes";

// ============================================================================
// SCHEDULER CONTEXT & EVALUATION
// ============================================================================

/**
 * Context information available to all schedule handlers during evaluation
 */
export interface ScheduleContext {
  currentTime: Date;
  prayerTimes?: PrayerTimes;
  screenData: ScreenData; // Add screenData to context
}

/**
 * Result of evaluating a single playlist's schedule
 */
export interface ScheduleEvaluationResult {
  isActive: boolean;
  effectivePriority: PriorityLevel;
  reason?: string;
  deactivationTime?: Date;
  nextEvaluationTime?: Date;
}

// ============================================================================
// SCHEDULE HANDLER INTERFACE
// ============================================================================

/**
 * Interface that all schedule type handlers must implement
 * Each ScheduleType enum value has a corresponding handler
 */
export interface IScheduleHandler {
  readonly scheduleType: ScheduleType;

  /**
   * Evaluate if playlist should be active given current context
   */
  evaluate(
    playlist: Playlist,
    context: ScheduleContext
  ): ScheduleEvaluationResult;

  /**
   * Get next time when this playlist's active state will change
   */
  getNextStateChange(playlist: Playlist, context: ScheduleContext): Date | null;

  /**
   * Validate playlist configuration for this schedule type
   * Returns array of error messages (empty if valid)
   */
  validateConfiguration(playlist: Playlist): string[];
}

// ============================================================================
// SCHEDULER CONFIGURATION
// ============================================================================

/**
 * Configuration options for EnhancedPlaylistScheduler
 */
export interface SchedulerConfiguration {
  /** If true, only highest priority playlists will be active */
  enablePriorityOverride: boolean;

  /** If true, higher priority playlists can interrupt lower ones */
  enableInterruption: boolean;

  /** Default minimum runtime for playlists (minutes) */
  defaultMinimumRunDuration: number;

  /** Default maximum runtime for playlists (minutes) */
  defaultMaximumRunDuration: number;

  /** How often to re-evaluate schedules (milliseconds) */
  evaluationInterval: number;

  /** Enable detailed console logging */
  enableLogging: boolean;
}

export const DEFAULT_SCHEDULER_CONFIG: SchedulerConfiguration = {
  enablePriorityOverride: true,
  enableInterruption: true,
  defaultMinimumRunDuration: 1,
  defaultMaximumRunDuration: 60,
  evaluationInterval: 30000, // 30 seconds
  enableLogging: false,
};

export const SCHEDULER_CONSTANTS = {
  MAX_CACHE_ENTRIES: 1000,
  CACHE_TTL_MINUTES: 5,
  MAX_EVALUATION_TIME_MS: 100,
  CONFLICT_RESOLUTION_TIMEOUT_MS: 50,
  STATE_CHANGE_DEBOUNCE_MS: 1000,
} as const;

// ============================================================================
// SCHEDULER RESULTS & STATUS
// ============================================================================

/**
 * Active playlist with runtime metadata
 */
export interface ActivePlaylist {
  playlist: Playlist;
  effectivePriority: PriorityLevel;
  deactivationTime?: Date;
}

/**
 * Status of a single playlist's schedule evaluation
 */
export interface PlaylistScheduleStatus {
  playlistId: number;
  playlistName: string;
  scheduleType: ScheduleType;
  isActive: boolean;
  reason?: string;
  effectivePriority: PriorityLevel;
  deactivationTime?: Date;
  nextEvaluationTime?: Date;
}

/**
 * Information about a resolved scheduling conflict
 */
export interface ConflictResolution {
  playlistId: number;
  playlistName: string;
  conflictReason: string;
  resolutionAction: "skipped" | "deferred" | "interrupted";
  winningPlaylistId?: number;
}

/**
 * Comprehensive result from scheduler evaluation
 */
export interface EnhancedSchedulerResult {
  /** Playlists that are currently active, sorted by priority */
  activePlaylists: ActivePlaylist[];

  /** Status of all playlists that were evaluated */
  allStatuses: PlaylistScheduleStatus[];

  /** Conflicts that were resolved during evaluation */
  conflictsResolved: ConflictResolution[];

  /** When this evaluation was performed */
  evaluationTime: Date;

  /** When the next evaluation should occur (if known) */
  nextEvaluationTime?: Date;

  /** How long the evaluation took (milliseconds) */
  executionTimeMs: number;
}

// ============================================================================
// ERRORS
// ============================================================================

/**
 * Custom error class for scheduler-related errors
 */
export class SchedulerError extends Error {
  constructor(message: string, public code: string, public details?: any) {
    super(message);
    this.name = "SchedulerError";

    // Maintains proper stack trace for where error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SchedulerError);
    }
  }
}

/**
 * Common scheduler error codes
 */
export enum SchedulerErrorCode {
  INVALID_CONFIGURATION = "INVALID_CONFIGURATION",
  NO_HANDLER_FOUND = "NO_HANDLER_FOUND",
  EVALUATION_TIMEOUT = "EVALUATION_TIMEOUT",
  PRAYER_TIMES_UNAVAILABLE = "PRAYER_TIMES_UNAVAILABLE",
  INVALID_SCHEDULE_DATA = "INVALID_SCHEDULE_DATA",
}
